import { withListOverflow } from "../../../v2/components/ListOverflow.jsx";
export const FeedTable = withListOverflow(FeedTableOwner);
export const OfficeMatrix = withListOverflow(OfficeMatrixOwner);
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { T } from "../../../app/tokens.js";
import { S } from "../../../app/styles.js";
import {
  fmtCount, fmtDate, fmtDateTime, fmtMonth, fmtRatio, fmtShortMonth, relativeAge,
} from "./coverageFormat.js";

export const STATUS_TONES = {
  Current: { bg: T.ragGreenBg, fg: T.ragGreenText, dot: T.greenDeep },
  Partial: { bg: T.ragAmberBg, fg: T.ragAmberText, dot: T.ragAmber },
  Stale: { bg: T.lightGray, fg: T.mid, dot: T.light },
  Error: { bg: T.ragRedBg, fg: T.ragRed, dot: T.ragRed },
};

export function coverageTone(ratio, threshold) {
  if (ratio === null || ratio === undefined) return { fg: T.mid, fill: T.utilizationTrack };
  if (ratio >= threshold) return { fg: T.ragGreenText, fill: T.greenDeep };
  return { fg: T.ragAmberText, fill: T.ragAmber };
}

export function officeIdentity(o) {
  return [o?.market, o?.office, o?.officeId != null ? `ID ${o.officeId}` : null].filter(Boolean).join(" · ");
}

export function periodNote(periodState, isComplete) {
  if (periodState === "elapsed") return "final as measured";
  if (periodState === "current") return "in progress";
  if (periodState === "future") return "future period";
  return isComplete ? "final as measured" : "incomplete";
}

export const cardStyle = { ...S.card, borderRadius: 14, overflow: "hidden", marginBottom: 16 };

export function SectionHead({ title, right, id }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "13px 16px", flexWrap: "wrap" }}>
      <span aria-hidden="true" style={{ width: 3, height: 15, background: T.accent, borderRadius: 2 }} />
      <h2 id={id} style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark, margin: 0 }}>{title}</h2>
      {right ? <span style={{ marginLeft: "auto", color: T.light, fontSize: T.fsXs5 }}>{right}</span> : null}
    </div>
  );
}

export function StatusChip({ status }) {
  const tone = STATUS_TONES[status] ?? STATUS_TONES.Stale;
  return (
    <span data-testid={`status-${status}`} style={{
      display: "inline-flex", alignItems: "center", gap: 6, fontSize: T.fsXs, fontWeight: T.fwSemibold,
      padding: "2px 10px", borderRadius: 6, background: tone.bg, color: tone.fg, whiteSpace: "nowrap",
    }}>
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: tone.dot }} />
      {status ?? "Unknown"}
    </span>
  );
}

const th = {
  textAlign: "left", fontSize: T.fsXxs, letterSpacing: ".04em", textTransform: "uppercase", color: T.mid,
  fontWeight: T.fwBold, padding: "9px 14px", borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
  background: T.tableHeadBg, whiteSpace: "nowrap",
};
const td = { padding: "11px 14px", borderBottom: `1px solid ${T.lightGray}`, verticalAlign: "top", fontSize: T.fsSm5, color: T.dark };
export const tableStyles = { th, td };

function TimeCell({ label, iso, asOf, testId }) {
  const text = fmtDateTime(iso);
  const age = text ? relativeAge(iso, asOf) : null;
  return (
    <div data-testid={testId} style={{ marginBottom: 4 }}>
      <span style={{ color: T.mid, fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</span>
      <div style={{ fontWeight: text ? T.fwSemibold : T.fwRegular, color: text ? T.dark : T.mid, fontStyle: text ? "normal" : "italic" }}>
        {text ?? "Unavailable"}
      </div>
      {age ? <div style={{ color: T.light, fontSize: T.fsXs }}>{age}</div> : null}
    </div>
  );
}

function FeedTableOwner({ feeds, asOf }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  if (!feeds?.length) {
    return <div data-testid="feeds-empty" style={{ ...S.emptyState, padding: 28 }}>No feeds are registered for coverage tracking.</div>;
  }
  return (
    <div style={{ overflowX: "auto" }} tabIndex={0} role="region" aria-label="Feeds table, scrolls horizontally">
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
        <thead><tr>
          <th style={th} scope="col">Data type</th><th style={th} scope="col">Source</th>
          <th style={th} scope="col">Cadence</th>
          <th style={th} scope="col">Status</th><th style={th} scope="col"><span style={{ position: "absolute", left: -9999 }}>Details</span></th>
        </tr></thead>
        <tbody>
          {feeds.map((f) => (
            <React.Fragment key={f.id}>
            <tr data-testid={`feed-row-${f.id}`} onClick={() => toggle(f.id)} style={{ cursor: "pointer", height: 40 }}>
              <td style={{ ...td, fontWeight: T.fwBold, verticalAlign: "middle" }}>{f.label}</td>
              <td style={{ ...td, color: T.mid }}>{f.source || "Unavailable"}</td>
              <td style={{ ...td, color: T.mid }}>{f.cadence || "Unavailable"}</td>
              <td style={td}><StatusChip status={f.status} /></td>
              <td style={{ ...td, width: 32 }}>
                <button type="button" data-testid={`feed-toggle-${f.id}`} aria-label={`Details for ${f.label}`}
                  aria-expanded={expanded.has(f.id)} aria-controls={`feed-evidence-${f.id}`}
                  onClick={(event) => { event.stopPropagation(); toggle(f.id); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, padding: 0, border: "none", borderRadius: 6, background: "transparent", color: T.mid, cursor: "pointer" }}>
                  <ChevronDown size={16} aria-hidden="true" style={{ transform: expanded.has(f.id) ? "rotate(180deg)" : "none", transition: "transform .18s" }} />
                </button>
              </td>
            </tr>
            <tr hidden={!expanded.has(f.id)} id={`feed-evidence-${f.id}`}>
              <td colSpan={5} style={{ ...td, padding: "16px 20px", background: T.hoverBg }}>
              {expanded.has(f.id) && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20 }}>
              <div>
                <TimeCell label="Last successful" iso={f.lastSuccessAt} asOf={asOf} testId={`feed-success-${f.id}`} />
                <TimeCell label="Last attempt" iso={f.lastAttemptAt} asOf={asOf} testId={`feed-attempt-${f.id}`} />
              </div>
              <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwBold, marginBottom: 8 }}>Notes and evidence</div>
                {f.notes ? <div style={{ marginBottom: f.evidence?.length ? 6 : 0 }}>{f.notes}</div> : null}
                {f.evidence?.length ? (
                  <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: "4px 10px", fontSize: T.fsXs }}>
                    {f.evidence.map((e, i) => (
                      <React.Fragment key={`${e.label}-${i}`}>
                        <dt style={{ color: T.mid }}>{e.label}</dt>
                        <dd style={{ margin: 0, color: T.dark }}>{e.value ?? "Unavailable"}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                ) : null}
                {!f.notes && !f.evidence?.length ? <span style={{ color: T.mid }}>Unavailable</span> : null}
              </div>
              </div>}
              </td>
            </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MonthBars({ months, selectedMonth, threshold, onSelect, disabled = false }) {
  if (!months?.length) return <div style={S.emptyState}>No monthly coverage in this window.</div>;
  return (
    <div>
      <div role="list" aria-label="Coverage by month" style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 168, padding: "8px 2px 0", overflowX: "auto" }}>
        {months.map((m) => {
          const tone = coverageTone(m.coverageRatio, threshold);
          const h = m.coverageRatio === null ? 4 : Math.max(4, Math.round(m.coverageRatio * 120));
          const sel = m.month === selectedMonth;
          const label = `${fmtMonth(m.month)}: ${fmtRatio(m.coverageRatio)} coverage, ${fmtCount(m.observedDays)} observed of ${fmtCount(m.eligibleDays)} eligible, ${fmtCount(m.missingDays)} missing, ${periodNote(m.periodState, m.isComplete)}`;
          return (
            <div role="listitem" key={m.month} style={{ flex: "1 0 38px", height: "100%" }}>
              <button
                type="button" data-testid={`month-bar-${m.month}`} aria-pressed={sel} aria-label={label}
                onClick={() => onSelect(m.month)} title={label} disabled={disabled}
                style={{
                  width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "flex-end", gap: 5, background: sel ? T.rowActive : "transparent",
                  border: "none", borderRadius: 8, padding: "4px 3px", cursor: "pointer",
                }}
              >
                <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: tone.fg }}>{fmtRatio(m.coverageRatio)}</span>
                <span aria-hidden="true" style={{
                  width: "100%", height: h, borderRadius: "5px 5px 0 0", background: tone.fill,
                  opacity: m.periodState === "current" ? 0.55 : 1,
                  backgroundImage: m.periodState === "current" ? `repeating-linear-gradient(135deg, transparent 0 4px, ${T.card} 4px 6px)` : "none",
                }} />
                <span style={{ fontSize: T.fsXxs, color: sel ? T.accent : T.mid, fontWeight: sel ? T.fwBold : T.fwRegular }}>{fmtShortMonth(m.month)}</span>
              </button>
            </div>
          );
        })}
      </div>
      <p style={{ color: T.light, fontSize: T.fsXs, margin: "8px 2px 0" }}>
        Coverage = observed / eligible office-days. Green meets the {fmtRatio(threshold)} threshold; hatched bars are periods still in progress. Elapsed months are final as measured, not guaranteed complete by calendar.
      </p>
    </div>
  );
}

export function GapList({ gaps, onOpen, disabled = false }) {
  if (!gaps?.length) {
    return <div data-testid="gaps-empty" style={{ padding: "18px 0", color: T.mid, fontSize: T.fsSm }}>No office has missing eligible days in this month.</div>;
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }} data-testid="gap-list">
      {gaps.map((g) => (
        <li key={g.officeId} style={{ borderBottom: `1px solid ${T.lightGray}` }}>
          <button type="button" data-testid={`gap-open-${g.officeId}`} onClick={() => onOpen(g)} disabled={disabled} aria-label={`Open day detail for ${officeIdentity(g)}`}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 2px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
            <span>
              <span style={{ fontWeight: T.fwSemibold, color: T.dark, fontSize: T.fsSm5 }}>{officeIdentity(g)}</span>
              <span style={{ display: "block", color: T.light, fontSize: T.fsXs }}>{[g.region, `${fmtCount(g.observedDays)} observed / ${fmtCount(g.eligibleDays)} eligible`].filter(Boolean).join(" · ")}</span>
            </span>
            <span style={{ marginLeft: "auto", fontWeight: T.fwBold, color: T.ragAmberText, fontSize: T.fsSm, whiteSpace: "nowrap" }}>
              {fmtCount(g.missingDays)} missing
            </span>
            <span aria-hidden="true" style={{ color: T.light }}>›</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function RefCell({ value, date }) {
  if (value === null || value === undefined) return <span style={{ color: T.mid, fontStyle: "italic" }}>Unavailable</span>;
  return <span>{fmtCount(value)}{date ? <span style={{ display: "block", color: T.light, fontSize: T.fsXxs }}>as of {fmtDate(date)}</span> : null}</span>;
}

function OfficeMatrixOwner({ offices, threshold, onOpen, periodState }) {
  if (!offices?.length) return <div data-testid="matrix-empty" style={{ ...S.emptyState, padding: 28 }}>No offices have eligible days in this month.</div>;
  return (
    <div style={{ overflowX: "auto" }} tabIndex={0} role="region" aria-label="Office coverage matrix, scrolls horizontally">
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }} data-testid="office-matrix">
        <thead><tr>
          {["Office", "Market / region", "Badging coverage", "Headcount", "Lease", "Seats / capacity"].map((h) => (
            <th key={h} scope="col" style={th}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {offices.map((o) => {
            const tone = coverageTone(o.coverageRatio, threshold);
            return (
              <tr key={o.officeId} data-testid={`matrix-row-${o.officeId}`}>
                <td style={td}>
                  <button type="button" data-testid={`matrix-open-${o.officeId}`} onClick={() => onOpen(o)}
                    style={{ background: "none", border: "none", padding: 0, color: T.accent, fontWeight: T.fwSemibold, cursor: "pointer", fontSize: T.fsSm5, textAlign: "left" }}>
                    {o.office}
                  </button>
                  <span style={{ display: "block", color: T.light, fontSize: T.fsXxs }}>ID {o.officeId}</span>
                </td>
                <td style={{ ...td, color: T.mid }}>{[o.market, o.region].filter(Boolean).join(" · ") || "—"}</td>
                <td style={{ ...td, color: tone.fg, fontWeight: T.fwBold }}>
                  {o.coverageRatio == null ? "Unavailable" : `${fmtRatio(o.coverageRatio)} · ${fmtCount(o.missingDays)} days missing`}
                  <span style={{ display: "block", color: T.light, fontSize: T.fsXxs, fontWeight: T.fwRegular }}>
                    {fmtCount(o.observedDays)} / {fmtCount(o.eligibleDays)} office-days · {periodNote(periodState, o.isComplete)}
                  </span>
                </td>
                <td style={{ ...td, textAlign: "right" }}><RefCell value={o.headcount} date={o.headcountDate} /></td>
                <td style={{ ...td, color: o.hasLease ? T.dark : T.ragAmberText }}>{o.hasLease ? "Effective lease" : "Unavailable / no effective lease"}</td>
                <td style={{ ...td, textAlign: "right" }}><RefCell value={o.seats} date={o.seatsDate} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
